"use client"

import { useState } from "react"
import { MessageSquare, Users, UserPlus, Search, Bell, CheckCheck, User, Circle } from "lucide-react"
import Contacts from "./Contacts"

const ConversationsList = ({
  conversations,
  setConversations,
  filteredConversations,
  activeView,
  setActiveView,
  contactsData,
  isContactsLoading,
  searchTerm,
  setSearchTerm,
  notificationsEnabled,
  handleEnableNotifications,
  currentUser,
  currentChatId,
  setCurrentChatId,
  isMobile,
  isMobileChatOpen,
  setIsMobileChatOpen,
  profileImageUrls,
  isUserOnline,
  typingStatus,
  getChatName,
  setSeenViaAPI,
  handleProfileClick,
  handleGroupInfoClick,
  setIsGroupModalOpen,
  isCreatingGroup,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const truncateText = (text, maxLength) => {
    if (!text) return ""
    if (text?.length <= maxLength) return text
    return text?.slice(0, maxLength) + "..."
  }

  const formatTime = (dateString) => {
    if (!dateString) return ""

    try {
      const date = new Date(dateString)

      if (isNaN(date.getTime())) {
        console.warn("Invalid date:", dateString)
        return "Today"
      }

      const now = new Date()

      if (now.toDateString() === date.toDateString()) {
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      }

      const yesterday = new Date(now)
      yesterday.setDate(yesterday.getDate() - 1)
      if (yesterday.toDateString() === date.toDateString()) {
        return "Yesterday"
      }

      const oneWeekAgo = new Date(now)
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)
      if (date > oneWeekAgo) {
        return date.toLocaleDateString([], { weekday: "short" })
      }

      return date.toLocaleDateString([], { month: "short", day: "numeric" })
    } catch (error) {
      console.error("Error formatting time:", error)
      return "Today"
    }
  }

  const formatMessageTimeForDisplay = (date) => {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

    const diffTime = today.getTime() - messageDate.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    const [hours, minutes] = [date.getHours(), date.getMinutes()]
    const period = hours >= 12 ? "PM" : "AM"
    const formattedHours = hours % 12 === 0 ? 12 : hours % 12

    if (diffDays === 0) {
      return `Today, ${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`
    } else if (diffDays === 1) {
      return `Yesterday, ${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`
    } else if (diffDays <= 7) {
      return `${date.toLocaleDateString([], { weekday: "short" })}, ${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`
    } else {
      const day = date.getDate()
      const month = date.getMonth() + 1
      const year = date.getFullYear()
      return `${day}/${month}/${year}, ${formattedHours}:${minutes.toString().padStart(2, "0")} ${period}`
    }
  }

  const handleContactSelect = (contact) => {
    const existingChat = conversations.find(
      (chat) =>
        chat.chatType === "pTp" &&
        chat.participants.some((p) => p.pid === contact.id) &&
        chat.participants.some((p) => p.pid === currentUser?.pid),
    )

    if (existingChat) {
      setCurrentChatId(existingChat._id)

      if (isMobile) {
        setIsMobileChatOpen(true)
      }

      if (currentUser) {
        setSeenViaAPI(existingChat._id, currentUser.pid)

        setConversations((prevConversations) =>
          prevConversations.map((chat) =>
            chat._id === existingChat._id
              ? {
                  ...chat,
                  participants: chat.participants?.map((p) => (p.pid === currentUser.pid ? { ...p, seen: "y" } : p)),
                }
              : chat,
          ),
        )
      }
    } else {
      // Check if there's already a temporary chat for this contact
      const existingTempChat = conversations.find(
        (chat) =>
          chat._id.startsWith("temp_") &&
          chat.chatType === "pTp" &&
          chat.participants.some((p) => p.pid === contact.id) &&
          chat.participants.some((p) => p.pid === currentUser?.pid),
      )

      if (existingTempChat) {
        // Use the existing temporary chat
        setCurrentChatId(existingTempChat._id)
        if (isMobile) {
          setIsMobileChatOpen(true)
        }
      } else {
        // Create a new temporary chat with proper structure
        const tempChatId = `temp_${Date.now()}_${contact.id}`
        const newChat = {
          _id: tempChatId,
          chatType: "pTp",
          participants: [
            {
              pid: currentUser?.pid || "",
              pName: currentUser?.pName || "",
              seen: "y",
              cid: `EaiO-fsOBcwCEsw=${Math.random().toString(36).substr(2, 9)}`,
              unseenCounter: 0,
            },
            {
              pid: contact.id,
              pName: contact.name,
              seen: "n", // Default to unseen for new conversations
              cid: `EaiO-fsOBcwCEsw=${Math.random().toString(36).substr(2, 9)}`,
              unseenCounter: 0,
            },
          ],
          latestMessage: null,
          latestMessageTime: new Date().toISOString(),
          latestMessageTimeFormatted: formatMessageTimeForDisplay(new Date()),
          latestMessageSendBy: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          __v: 0,
        }

        setCurrentChatId(newChat._id)
        setConversations((prevConversations) => [newChat, ...prevConversations])

        if (isMobile) {
          setIsMobileChatOpen(true)
        }
      }
    }
    setActiveView("messages")
    setIsMobileMenuOpen(false)
  }

  const handleChatSelect = (chat) => {
    setCurrentChatId(chat._id)

    if (isMobile) {
      setIsMobileChatOpen(true)
    }

    if (currentUser) {
      setSeenViaAPI(chat._id, currentUser.pid)

      const isUnseen = chat.participants.find((p) => p.pid === currentUser?.pid)?.seen === "n"
      if (isUnseen) {
        setConversations((prevConversations) =>
          prevConversations?.map((c) =>
            c._id === chat._id
              ? {
                  ...c,
                  participants: c.participants?.map((p) => (p.pid === currentUser?.pid ? { ...p, seen: "y" } : p)),
                }
              : c,
          ),
        )
      }
    }
    setIsMobileMenuOpen(false)
  }

  return (
    <div
      className={`w-full md:w-96 bg-white shadow-lg ${
        isMobile && isMobileChatOpen ? "hidden" : "block"
      } md:flex-shrink-0 ${isMobile ? "mobile-sidebar" : "h-screen"} flex flex-col overflow-hidden`}
    >
      {/* Search bar */}
      <div className="px-3 py-2 bg-white border-b border-gray-200 flex items-center flex-shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" size={18} />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-full text-sm focus:outline-none border border-gray-200"
          />
        </div>
        <button
          onClick={handleEnableNotifications}
          className={`ml-2 p-2 rounded-full ${notificationsEnabled ? "text-blue-500" : "text-gray-400"}`}
          title={notificationsEnabled ? "Notifications enabled" : "Enable notifications"}
        >
          <Bell size={20} />
        </button>
      </div>

      {/* Mobile tabs */}
      <div className="md:hidden border-b border-gray-200 flex-shrink-0">
        <div className="flex text-sm">
          <button
            className={`flex-1 py-3 px-4 text-center text-sm font-medium transition duration-150 ease-in-out ${
              activeView === "messages"
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveView("messages")}
          >
            <MessageSquare size={16} className="inline-block mr-1" />
            Messages
          </button>
          <button
            className={`flex-1 py-3 px-4 text-center text-sm font-medium transition duration-150 ease-in-out ${
              activeView === "groups"
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveView("groups")}
          >
            <UserPlus size={16} className="inline-block mr-1" />
            Groups
          </button>
          <button
            className={`flex-1 py-3 px-4 text-center text-sm font-medium transition duration-150 ease-in-out ${
              activeView === "contacts"
                ? "text-blue-600 border-b-2 border-blue-600 bg-blue-50"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveView("contacts")}
          >
            <Users size={16} className="inline-block mr-1" />
            Contacts
          </button>
        </div>
      </div>

      {/* Desktop header */}
      <div className="py-2 px-6 border-b border-gray-200 flex-shrink-0 hidden md:block">
        <div className="flex border-b border-gray-200 text-sm md:text-base">
          <button
            className={`flex-1 py-2 px-4 text-center text-sm font-medium transition duration-150 ease-in-out ${
              activeView === "messages"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveView("messages")}
          >
            <MessageSquare size={18} className="inline-block mr-1" />
            Messages
          </button>
          <button
            className={`flex-1 py-2 px-4 text-center text-sm font-medium transition duration-150 ease-in-out ${
              activeView === "groups" ? "text-blue-600 border-b-2 border-blue-600" : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveView("groups")}
          >
            <UserPlus size={18} className="inline-block mr-1" />
            Groups
          </button>
          <button
            className={`flex-1 py-2 px-4 text-center text-sm font-medium transition duration-150 ease-in-out ${
              activeView === "contacts"
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-600 hover:text-gray-800"
            }`}
            onClick={() => setActiveView("contacts")}
          >
            <Users size={18} className="inline-block mr-1" />
            Contacts
          </button>
        </div>
      </div>

      {/* Conversations list */}
      <div
        className={`overflow-y-auto flex-1 custom-scrollbar ${isMobile ? "mobile-conversations-list" : "h-[calc(100vh-120px)]"}`}
      >
        {activeView === "messages" &&
          (filteredConversations?.length > 0 ? (
            filteredConversations?.map((chat) => {
              const currentUserParticipant = chat.participants.find((p) => p.pid === currentUser?.pid)
              const otherParticipant = chat.participants.find((p) => p.pid !== currentUser?.pid)
              const isUnseen = currentUserParticipant && currentUserParticipant.seen === "n"
              const isOtherUserOnline = isUserOnline(otherParticipant?.pid)

              return (
                <div
                  key={chat._id}
                  className={`flex items-center p-4 cursor-pointer hover:bg-gray-50 transition duration-150 ease-in-out border-b border-gray-100 ${
                    currentChatId === chat._id ? "bg-gray-100" : ""
                  }`}
                  onClick={() => handleChatSelect(chat)}
                >
                  {/* Profile image with click handler - ONLY profile photo opens modal */}
                  <div
                    className="profile-clickable profile-container"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      console.log("Conversation profile clicked:", { chatType: chat.chatType, otherParticipant })
                      if (chat.chatType === "pTe") {
                        handleGroupInfoClick(chat)
                      } else if (otherParticipant) {
                        handleProfileClick({
                          ...otherParticipant,
                          profileImageUrl: profileImageUrls[otherParticipant.pid] || null,
                        })
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {chat.chatType === "pTp" && otherParticipant ? (
                      <div className="relative mr-4 w-12 h-12">
                        {otherParticipant.profilePhotoS3Uri && profileImageUrls[otherParticipant.pid] ? (
                          <img
                            src={profileImageUrls[otherParticipant.pid] || "/placeholder.svg"}
                            alt={getChatName(chat)}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-6 w-6 text-gray-500" />
                          </div>
                        )}

                        {isUserOnline(otherParticipant.pid) && (
                          <>
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-400 rounded-full animate-ping pointer-events-none" />
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white pointer-events-none" />
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
                        <Users className="h-6 w-6 text-gray-500" />
                      </div>
                    )}
                  </div>

                  <div className="flex-grow">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        {/* Removed click handler from conversation name */}
                        <h3 className="font-semibold text-gray-800">{getChatName(chat)}</h3>
                        {chat.chatType === "pTp" && isOtherUserOnline && (
                          <Circle size={8} className="text-green-500 fill-current ml-2" />
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {chat.latestMessageTimeFormatted
                          ? (() => {
                              try {
                                const [datePart, timePart] = chat.latestMessageTimeFormatted.split(", ")
                                const [day, month, year] = datePart.split("/")
                                const date = new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`)

                                const now = new Date()
                                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                                const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

                                const diffTime = today.getTime() - messageDate.getTime()
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

                                if (diffDays === 0) {
                                  const [time, period] = timePart.split(" ")
                                  const [hours, minutes] = time.split(":")
                                  return `${hours}:${minutes} ${period}`
                                } else if (diffDays === 1) {
                                  return "Yesterday"
                                } else if (diffDays <= 7) {
                                  return date.toLocaleDateString([], { weekday: "short" })
                                } else {
                                  return `${day}/${month}`
                                }
                              } catch (error) {
                                console.error("Error parsing latestMessageTimeFormatted:", error)
                                return "Invalid date"
                              }
                            })()
                          : chat.latestMessageTime
                            ? formatTime(chat.latestMessageTime)
                            : chat.createdAt
                              ? formatTime(chat.createdAt)
                              : ""}
                      </span>
                    </div>

                    <div className="flex items-center">
                      {chat.latestMessage && (
                        <div className="flex items-center mr-1">
                          {chat.latestMessageSendBy && chat.latestMessageSendBy.pid === currentUser?.pid
                            ? (() => {
                                const recipients = chat.participants.filter((p) => p.pid !== currentUser?.pid)
                                const hasUnseenMessages = recipients.some((p) => p.unseenCounter > 0)

                                if (hasUnseenMessages) {
                                  return <CheckCheck size={14} className="text-gray-400" />
                                } else {
                                  return <CheckCheck size={14} className="text-blue-500" />
                                }
                              })()
                            : null}
                        </div>
                      )}
                      <p
                        className={`text-sm text-gray-600 flex-grow ${isMobile ? "mobile-conversation-text" : ""} truncate`}
                      >
                        {typingStatus[chat._id] &&
                        Object.values(typingStatus[chat._id]).some((status) => status && status.isTyping) ? (
                          <span className="text-blue-500 italic">
                            {(() => {
                              const typingUsers = Object.values(typingStatus[chat._id]).filter(
                                (status) => status && status.isTyping,
                              )

                              if (typingUsers.length === 1) {
                                return `${typingUsers[0]?.pName} is typing...`
                              } else if (typingUsers.length === 2) {
                                return `${typingUsers[0]?.pName} and ${typingUsers[1]?.pName} are typing...`
                              } else {
                                return `${typingUsers.length} people are typing...`
                              }
                            })()}
                          </span>
                        ) : chat.latestMessage ? (
                          truncateText(chat.latestMessage, isMobile ? 25 : 30)
                        ) : chat._id.startsWith("temp_") ? (
                          <span className="text-gray-400 italic">Start a conversation...</span>
                        ) : (
                          "No messages yet"
                        )}
                      </p>
                      {chat.latestMessageSendBy && chat.latestMessageSendBy.pid !== currentUser?.pid && isUnseen && (
                        <span className="bg-blue-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center ml-2">
                          {currentUserParticipant?.unseenCounter || 1}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <div className="p-4 text-center text-gray-500">
              {searchTerm ? "No matching conversations found." : "No conversations yet. Start chatting!"}
            </div>
          ))}

        {activeView === "groups" && (
          <>
            <div className="p-4">
              {isCreatingGroup ? (
                <div className="w-full py-2 px-4 bg-blue-100 text-blue-800 rounded-lg text-center">
                  Creating group...
                </div>
              ) : (
                <button
                  className="w-full py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  onClick={() => setIsGroupModalOpen(true)}
                >
                  Create New Group
                </button>
              )}
            </div>
            {conversations
              .filter((chat) => chat.chatType === "pTe")
              ?.map((chat) => (
                <div
                  key={chat._id}
                  className={`flex items-center p-4 cursor-pointer hover:bg-gray-50 transition duration-150 ease-in-out border-b border-gray-100 ${
                    currentChatId === chat._id ? "bg-gray-100" : ""
                  }`}
                  onClick={() => handleChatSelect(chat)}
                >
                  {/* Group profile image - ONLY profile photo opens modal */}
                  <div
                    className="profile-clickable"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleGroupInfoClick(chat)
                    }}
                  >
                    <div className="w-12 h-12 rounded-full mr-4 bg-gray-200 flex items-center justify-center">
                      <Users className="h-6 w-6 text-gray-500" />
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="flex justify-between items-center">
                      {/* Removed click handler from group name */}
                      <h3 className="font-semibold text-gray-800">{getChatName(chat)}</h3>
                      <span className="text-xs text-gray-500">
                        {chat.latestMessageTimeFormatted
                          ? (() => {
                              try {
                                const [datePart, timePart] = chat.latestMessageTimeFormatted.split(", ")
                                const [day, month, year] = datePart.split("/")
                                const date = new Date(`${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`)

                                const now = new Date()
                                const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
                                const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())

                                const diffTime = today.getTime() - messageDate.getTime()
                                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

                                if (diffDays === 0) {
                                  const [time, period] = timePart.split(" ")
                                  const [hours, minutes] = time.split(":")
                                  return `${hours}:${minutes} ${period}`
                                } else if (diffDays === 1) {
                                  return "Yesterday"
                                } else if (diffDays <= 7) {
                                  return date.toLocaleDateString([], { weekday: "short" })
                                } else {
                                  return `${day}/${month}`
                                }
                              } catch (error) {
                                console.error("Error parsing latestMessageTimeFormatted:", error)
                                return "Invalid date"
                              }
                            })()
                          : chat.createdAt
                            ? formatTime(chat.createdAt)
                            : ""}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {typingStatus[chat._id] &&
                      Object.values(typingStatus[chat._id]).some((status) => status && status.isTyping) ? (
                        <span className="text-blue-500 italic">
                          {(() => {
                            const typingUsers = Object.values(typingStatus[chat._id]).filter(
                              (status) => status && status.isTyping,
                            )

                            if (typingUsers.length === 1) {
                              return `${typingUsers[0]?.pName} is typing...`
                            } else if (typingUsers.length === 2) {
                              return `${typingUsers[0]?.pName} and ${typingUsers[1]?.pName} are typing...`
                            } else {
                              return `${typingUsers.length} people are typing...`
                            }
                          })()}
                        </span>
                      ) : chat.latestMessage ? (
                        truncateText(chat.latestMessage, 30)
                      ) : (
                        "No messages yet"
                      )}
                    </p>
                  </div>
                </div>
              ))}
          </>
        )}

        {activeView === "contacts" && (
          <div className={isMobile ? "mobile-contacts-list" : ""}>
            <Contacts
              contactsData={contactsData}
              onContactSelect={handleContactSelect}
              currentUserId={currentUser?.pid}
              isLoading={isContactsLoading}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ConversationsList
